import type { SubmitHandler } from "react-hook-form"

import { FILE_UPLOAD_TAKES_TIME_MESSAGE } from "@/components/upload/constants"
import type {
  CompanyRegistration,
  CompanyRegistrationCreateInput,
  CompanyRegistrationCreateSuccess,
  CompanyRegistrationUpdateInput,
  CompanyRegistrationUpdateSuccess,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import type { RegistrationType } from "@/screens/CompanySettings/Registration/forms/registrationSchema"
import useCompanyRegistration from "@/screens/CompanySettings/Registration/hooks/useCompanyRegistration"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseRegistrationActionsProps {
  onSuccess?: (data: CompanyRegistration) => Promise<void>
  registrationType: RegistrationType
}

const useRegistrationActions = ({
  onSuccess,
  registrationType,
}: UseRegistrationActionsProps): {
  registrationList: CompanyRegistration[]
  addRegistrationData: SubmitHandler<CompanyRegistrationCreateInput>
  updateRegistrationData: SubmitHandler<CompanyRegistrationUpdateInput>
  deleteRegistrationData: (companyRegistrationId: string) => Promise<void>
} => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const { addRegistration, updateRegistration, deleteRegistration, registrationList } = useCompanyRegistration()

  const addRegistrationData = async (input: CompanyRegistrationCreateInput) => {
    const { error, data } = await addRegistration({ input })

    const hasError =
      error?.message ||
      (data?.companyRegistration.create.__typename === "CompanyRegistrationCreateFailure" &&
        Boolean(data?.companyRegistration.create.error))

    if (hasError) {
      notify({
        type: NotificationType.Error,
        silent: false,
        message: error?.message ?? `Failed to create registration`,
      })
      return
    }

    await onSuccess?.((data?.companyRegistration.create as CompanyRegistrationCreateSuccess).companyRegistration)
    notify({
      type: NotificationType.Success,
      silent: false,
      content: `Company ${registrationType!.toLowerCase()} registration successfully added. ${FILE_UPLOAD_TAKES_TIME_MESSAGE}`,
    })
  }

  const updateRegistrationData = async (input: CompanyRegistrationUpdateInput) => {
    const { error, data } = await updateRegistration({ input })

    const hasError =
      error?.message ||
      (data?.companyRegistration.update.__typename === "CompanyRegistrationFailure" &&
        Boolean(data?.companyRegistration.update.error))

    if (hasError) {
      notify({
        type: NotificationType.Error,
        silent: false,
        message: error?.message ?? `Failed to update registration`,
      })
      return
    }

    await onSuccess?.((data?.companyRegistration.update as CompanyRegistrationUpdateSuccess).companyRegistration)
    notify({
      type: NotificationType.Success,
      silent: false,
      content: `Company ${registrationType!.toLowerCase()} registration successfully updated`,
    })
  }

  const deleteRegistrationData = async (companyRegistrationId: string) => {
    const { error, data } = await deleteRegistration({ input: { companyRegistrationId } })

    const hasError =
      error?.message ||
      (data?.companyRegistration.delete.__typename === "CompanyRegistrationDeleteFailure" &&
        Boolean(data?.companyRegistration.delete.error))

    if (hasError) {
      notify({
        type: NotificationType.Error,
        silent: false,
        message: error?.message ?? `Failed to remove registration`,
      })
      return
    }

    await onSuccess?.(undefined as any)
    notify({
      type: NotificationType.Success,
      silent: false,
      content: `Company ${registrationType!.toLowerCase()} registration successfully removed`,
    })
  }

  return {
    addRegistrationData,
    updateRegistrationData,
    deleteRegistrationData,
    registrationList,
  }
}

export default useRegistrationActions
